import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SaveIcon from "@mui/icons-material/Save";
import { saveBrokerTransaction } from "../../../Services/BrokerTransactionsApi";

/*
  Props:
  - open: boolean
  - onClose: () => void
  - brokerId: number|string (يمرّر تلقائياً من الصفحة الرئيسية)
  - defaults: { amount, date, type, note }  // اختياري
  - onSuccess: (res, payload) => void  // يستدعى بعد النجاح
*/

const MakeBrokerTransactionDialog = ({ open, onClose, brokerId = null, defaults = {}, onSuccess }) => {
  const [form, setForm] = useState({
    broker_id: brokerId ?? defaults.broker_id ?? "",
    type: defaults.type ?? "deposite",
    date: defaults.date ?? "",
    amount: defaults.amount ?? "",
    note: defaults.note ?? "",
    image: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm((p) => ({
      broker_id: brokerId ?? defaults.broker_id ?? "",
      type: defaults.type ?? p.type ?? "deposite",
      date: defaults.date ?? p.date ?? "",
      amount: defaults.amount ?? p.amount ?? "",
      note: defaults.note ?? p.note ?? "",
      image: null,
    }));
    setError("");
  }, [open, brokerId, defaults]);

  const handleChange = (key) => (e) => {
    const value = key === "image" ? (e.target.files?.[0] ?? null) : e.target.value;
    setForm((s) => ({ ...s, [key]: value }));
  };

  const validate = () => {
    if (!form.broker_id) return "معرّف الموزع مطلوب.";
    if (!form.amount) return "المبلغ مطلوب.";
    if (!form.type) return "نوع العملية مطلوب.";
    if (!form.date) return "التاريخ مطلوب.";
    return null;
  };

  const handleSubmit = async () => {
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setLoading(true);
    try {
      // جهّز payload؛ saveBrokerTransaction سيبني FormData داخلياً إذا مررنا كائن يحتوي ملف
      const payload = {
        broker_id: form.broker_id,
        type: form.type,
        date: form.date,
        amount: form.amount,
        note: form.note,
      };
      if (form.image) payload.image = form.image;

      const token = localStorage.getItem("authToken") || null;
      const res = await saveBrokerTransaction(payload, token);
      onSuccess && onSuccess(res, payload);
      // تنظيف وإغلاق
      setForm((s) => ({ ...s, amount: "", note: "", image: null }));
      onClose && onClose();
    } catch (err) {
      console.error("saveBrokerTransaction error:", err);
      const msg = err?.response?.data?.message || err?.message || "فشل إنشاء المعاملة";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography>إنشاء معاملة للموزع</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="معرف الموزع"
            value={form.broker_id}
            fullWidth
            disabled
          />

          <FormControl size="small" fullWidth>
            <InputLabel id="tx-type-label">النوع</InputLabel>
            <Select
              labelId="tx-type-label"
              value={form.type}
              label="النوع"
              onChange={(e) => setForm((s) => ({ ...s, type: e.target.value }))}
            >
              <MenuItem value="deposite">إيداع</MenuItem>
              <MenuItem value="withdraw">سحب</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="المبلغ"
            value={form.amount}
            onChange={(e) => setForm((s) => ({ ...s, amount: e.target.value }))}
            fullWidth
            type="number"
            inputProps={{ step: "0.01" }}
            size="small"
          />

          <TextField
            label="تاريخ العملية"
            type="date"
            value={form.date}
            onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />

          <TextField
            label="ملاحظة"
            value={form.note}
            onChange={(e) => setForm((s) => ({ ...s, note: e.target.value }))}
            fullWidth
            multiline
            rows={3}
            size="small"
          />

          <Box>
            <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
              رفع ملف (اختياري)
              <input hidden type="file" accept="image/*,application/pdf" onChange={handleChange("image")} />
            </Button>
            {form.image && (
              <Typography variant="caption" sx={{ ml: 2 }}>{form.image.name}</Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>إلغاء</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          disabled={loading}
        >
          {loading ? "جاري الحفظ..." : "حفظ العملية"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MakeBrokerTransactionDialog;
